import { useTranslate } from "@tolgee/react";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { AddressDrawer } from "@/components/AddressDrawer/AddressDrawer";
import { CollapseSection } from "@/components/CollapseSection/CollapseSection";
import {
  type AddressReadDto,
  OrderItemUpdateCommandDispatchTypeEnum,
} from "@/config/apiClient";
import { redispatchQuantities } from "@/helpers/redispatchQuantities";
import { type OrderItemConfigurationSchema } from "../OrderItemConfigForm.schema";
import { DrawerDispatchFile } from "./_partials/DrawerDispatchFile";
import { EmptyAddressBlock } from "./_partials/EmptyAddressBlock";
import { FileAddressBlock } from "./_partials/FileAddressBlock";
import { ManualAddressBlock } from "./_partials/ManualAddressBlock";
import { ModalEditVersions } from "./_partials/ModalEditVersions";
import { type DeliverySectionProps } from "./DeliverySection.types";

export const DeliverySection = ({
  isOpen,
  isModificationRequest,
  onOpenChange,
}: DeliverySectionProps) => {
  const { t } = useTranslate();

  const methods = useFormContext<OrderItemConfigurationSchema>();

  const [versionToEdit, setVersionToEdit] = useState<string | undefined>(
    undefined
  );
  const [addressToEdit, setAddressToEdit] = useState<
    Omit<AddressReadDto, "createdAt"> | undefined
  >(undefined);
  const [isDrawerDispatchFileOpen, setIsDrawerDispatchFileOpen] =
    useState(false);
  const [isManualAddressDrawerOpen, setIsManualAddressDrawerOpen] =
    useState(false);

  // Derive drawer open state: open when manually opened OR when editing an address
  const isAddressDrawerOpen = isManualAddressDrawerOpen || !!addressToEdit;

  const dispatchFiles = methods.watch("dispatchFiles");
  const dispatchType = methods.watch("dispatchType");
  const dispacthesFirstVersion = methods.watch("versions.0.dispatches");
  const totalQuantity = methods.watch("totalQuantity");
  const versions = methods.watch("versions");

  const selectedAddresses: AddressReadDto[] = useMemo(
    () =>
      dispacthesFirstVersion
        .filter((dispatch) => !!dispatch.address.address)
        .map((dispatch) => {
          const {
            id: _id,
            recipientName,
            complementaryInfo,
            phoneNumber,
            ...rest
          } = dispatch.address;
          return {
            ...rest,
            id: "",
            recipientName: recipientName ?? "",
            complementaryInfo: complementaryInfo ?? "",
            phoneNumber: phoneNumber ?? "",
            createdAt: "",
          };
        }),
    [dispacthesFirstVersion]
  );

  const isManualDispatch = useMemo(
    () =>
      dispatchType !== OrderItemUpdateCommandDispatchTypeEnum.DispatchFiles &&
      !!selectedAddresses.length &&
      !!dispatchFiles?.[0] &&
      (!dispatchFiles?.[0].fileSignedId || !!dispatchFiles?.[0].blobId),
    [selectedAddresses, dispatchFiles, dispatchType]
  );

  const isFileDispatch = useMemo(
    () =>
      dispatchType === OrderItemUpdateCommandDispatchTypeEnum.DispatchFiles &&
      !selectedAddresses.length &&
      (!!dispatchFiles?.[0]?.fileSignedId || !!dispatchFiles?.[0]?.blobId),
    [selectedAddresses, dispatchFiles, dispatchType]
  );

  const isEmptyDispatch = !isManualDispatch && !isFileDispatch;

  const collapseSectionLink: { link?: string; linkAction?: () => void } =
    useMemo(() => {
      if (isEmptyDispatch) {
        return { link: undefined, linkAction: undefined };
      }

      if (isManualDispatch) {
        return {
          link: t("orderItemConfig.section.deliveryLinkFile"),
          linkAction: () => setIsDrawerDispatchFileOpen(true),
        };
      }

      return {
        link: t("orderItemConfig.section.deliveryLinkAddress"),
        linkAction: () => setIsManualAddressDrawerOpen(true),
      };
    }, [isEmptyDispatch, isManualDispatch, t]);

  const handleSelectAddress = (addresses: AddressReadDto[] | []) => {
    // Do a dispatch when no selectedAddresses
    if (!addresses.length) {
      versions.map((_, index) =>
        methods.setValue(
          `versions.${index}.dispatches`,
          [
            {
              quantity: index === 0 ? totalQuantity : 0,
              address: {
                id: "",
                name: "",
                recipientName: "",
                address: "",
                complementaryInfo: "",
                postalCode: "",
                city: "",
                country: "",
                phoneNumber: "",
              },
            },
          ],
          { shouldDirty: true }
        )
      );
    }

    // Do a redispatch when adding first addresses
    else if (
      !!addresses.length &&
      versions[0].dispatches.length === 1 &&
      !versions[0].dispatches[0].address.address
    ) {
      versions.forEach((_, index) => {
        methods.setValue(
          `versions.${index}.dispatches`,
          addresses.map((address) => ({ address: address, quantity: 0 }))
        );
      });
      return redispatchQuantities({
        totalQuantities: totalQuantity,
        methods: methods,
      });
    }

    // Update dispatch when selectedAddresses change
    else {
      versions.map((version, indexVersion) => {
        const versionTotalQuantity = version.dispatches.reduce(
          (sum, d) => sum + (d.quantity || 0),
          0
        );

        // Define Quantity (keep previous or set 0)
        const quantitiesArray = addresses.map((address, index) => {
          if (
            versions[indexVersion].dispatches.find(
              (addressDispatch) =>
                addressDispatch.address.address === address.address &&
                addressDispatch.address.postalCode === address.postalCode &&
                addressDispatch.address.recipientName === address.recipientName
            )
          ) {
            return (
              versions[indexVersion].dispatches.find(
                (addressDispatch) =>
                  addressDispatch.address.address === address.address &&
                  addressDispatch.address.postalCode === address.postalCode &&
                  addressDispatch.address.recipientName ===
                    address.recipientName
              )?.quantity ?? 0
            );
          } else {
            return index === 0 ? versionTotalQuantity : 0;
          }
        });

        return methods.setValue(
          `versions.${indexVersion}.dispatches`,
          addresses.map((address, index) => ({
            address: address,
            quantity: quantitiesArray[index],
          })),
          { shouldDirty: true, shouldValidate: true }
        );
      });

      return methods.setValue(
        "totalQuantity",
        versions.reduce((sum, v) => {
          if (v.isDeleted) {
            return sum;
          }
          const innerSum = v.dispatches.reduce(
            (s, d) => s + (d.quantity || 0),
            0
          );
          return sum + innerSum;
        }, 0)
      );
    }
  };

  return (
    <CollapseSection
      size="medium"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      variant="transparent"
      title={t("orderItemConfig.section.deliveryTitle")}
      link={collapseSectionLink.link}
      linkAction={collapseSectionLink.linkAction}
    >
      {/* Empty Address */}
      {!isManualDispatch && !isFileDispatch && (
        <EmptyAddressBlock
          onPressEditVersion={() => setVersionToEdit("tab_0")}
          onPressAddAddress={() => setIsManualAddressDrawerOpen(true)}
          onPressImportDispatchFile={() => setIsDrawerDispatchFileOpen(true)}
        />
      )}

      {/* Manual Dispatch */}
      {isManualDispatch && (
        <ManualAddressBlock
          isModificationRequest={isModificationRequest}
          onPressEditVersion={setVersionToEdit}
          onPressAddAddress={() => setIsManualAddressDrawerOpen(true)}
          onPressEditAddress={setAddressToEdit}
          selectedAddresses={selectedAddresses}
          onChangeSelectedAddresses={handleSelectAddress}
        />
      )}

      {/* File Dispatch */}
      {isFileDispatch && (
        <FileAddressBlock
          onRemoveFile={() => handleSelectAddress([])}
          onPressEditVersion={() => setVersionToEdit("tab_0")}
        />
      )}

      {/** * Drawer Dispatch Files ***/}
      <DrawerDispatchFile
        isOpen={isDrawerDispatchFileOpen}
        onRemoveFile={() => handleSelectAddress([])}
        onOpenChange={() =>
          setIsDrawerDispatchFileOpen(!isDrawerDispatchFileOpen)
        }
      />

      {/** * Modal Edit Versions ***/}
      {!!versionToEdit && (
        <ModalEditVersions
          isOpen
          isModificationRequest={isModificationRequest}
          activeTab={versionToEdit}
          onOpenChange={() => setVersionToEdit(undefined)}
        />
      )}

      {/** * Addresses Sidebar ***/}
      <AddressDrawer
        selectedAddresses={selectedAddresses}
        onDeleteAddressCallback={(addressToDelete) => {
          handleSelectAddress(
            selectedAddresses.filter(
              (address) =>
                address.name !== addressToDelete.name &&
                address.recipientName !== addressToDelete.recipientName &&
                address.postalCode !== addressToDelete.postalCode
            )
          );
        }}
        onSelectAddress={(address) => {
          handleSelectAddress(address);
          methods.setValue("dispatchFiles.0.fileSignedId", "");
          methods.setValue("dispatchFiles.0.isDeleted", true);
          methods.setValue(
            "dispatchType",
            address.length > 1
              ? OrderItemUpdateCommandDispatchTypeEnum.ManualDispatch
              : OrderItemUpdateCommandDispatchTypeEnum.SingleDispatch
          );
        }}
        addressToBeEdited={addressToEdit}
        isOpen={isAddressDrawerOpen}
        onOpenChange={() => {
          setIsManualAddressDrawerOpen(false);
          setAddressToEdit(undefined);
        }}
      />
    </CollapseSection>
  );
};
